import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleDetailsData, articlesData, categoriesData } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = id ? articleDetailsData[id] : null;
  const preview = id ? articlesData.find((a) => a.id === id) : null;
  const data = article || preview;

  useEffect(() => {
    if (data) {
      document.title = `${data.title} | luluca官网`;
    }
  }, [data]);

  if (!data) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/categories" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
              返回分类浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoriesData.find((c) => c.id === data.categoryId);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    description: data.excerpt,
    author: {
      '@type': 'Person',
      name: data.author,
    },
    datePublished: data.date,
    image: data.coverImage,
    url: `https://luluca.com/article/${data.id}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: 'luluca',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '分类浏览', to: '/categories' },
            ...(category ? [{ label: category.name, to: `/categories?cat=${category.id}` }] : []),
            { label: data.title },
          ]}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs text-foreground-500">{data.date}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">{data.readTime}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">{data.author}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {data.title}
            </h1>
            <div className="flex flex-wrap gap-1.5 mb-6">
              {data.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src={data.coverImage}
                alt={data.title}
                className="w-full h-full object-cover"
              />
            </div>
          </header>

          {'contentHtml' in data ? (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: (data as typeof articleDetailsData[string]).contentHtml }}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-foreground-500 mb-4">完整文章内容即将上线</p>
              <Link
                to="/categories"
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                浏览更多文章
              </Link>
            </div>
          )}

          <footer className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/categories"
                className="inline-flex items-center text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line mr-1.5"></i>
                返回分类浏览
              </Link>
              <div className="flex items-center gap-4">
                <span className="text-sm text-foreground-500">分享：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link-m"></i>
                </a>
              </div>
            </div>
          </footer>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}